'use client';

import { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitType from 'split-type';
import styles from '../styles/Newsletter.module.css';

const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const headingRef = useRef(null);
  const formRef = useRef(null);
  const buttonRef = useRef(null);
  
  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    
    const heading = headingRef.current;
    const form = formRef.current;
    const button = buttonRef.current;
    
    if (!heading || !form || !button) return;
    
    // Split text for animation
    const split = new SplitType(heading, {
      types: "words, chars",
    });
    
    // Create the timeline with ScrollTrigger
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: `#newsletter`,
        start: "top 70%",
        end: "+=50%",
        scrub: 0.5,
      },
    });
    
    // Animate each character
    tl.set(split.chars, {
      duration: 0.3,
      color: "#fff",
      stagger: 0.05,
    }, 0.1);
    
    // Animate form
    gsap.from(form, {
      scrollTrigger: {
        trigger: form,
        start: 'top 80%',
        end: 'center center',
      },
      y: 50,
      opacity: 0,
      duration: 0.8,
      ease: 'power3.out'
    });
    
    // Button hover animation
    if (button) {
      button.addEventListener('mouseenter', () => {
        gsap.to(button, {
          scale: 1.05,
          duration: 0.3,
          ease: 'power2.out'
        });
      });
      
      button.addEventListener('mouseleave', () => {
        gsap.to(button, {
          scale: 1,
          duration: 0.3,
          ease: 'power2.out'
        });
      });
    }
    
    return () => {
      if (button) {
        button.removeEventListener('mouseenter', () => {});
        button.removeEventListener('mouseleave', () => {});
      }
    };
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return; // Basic validation
    setIsSubmitting(true);
    setSubmitStatus(null);
    setStatusMessage('');

    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      // Parse the response
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || `HTTP error! status: ${response.status}`);
      }

      // Set success status
      setSubmitStatus('success');
      setStatusMessage(result.message || 'You\'ve successfully subscribed!');
      setEmail('');
    } catch (error) {
      console.error('Subscription failed:', error);
      setSubmitStatus('error');
      setStatusMessage(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
      // Reset status after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
        setStatusMessage('');
      }, 5000);
    }
  };
  
  return (
    <section className={styles.newsletter} id="newsletter">
      <div className={styles.newsletterContainer}>
        <div className={styles.newsletterHeader}>
          <p className="callout">Stay Updated</p>
          <h2 ref={headingRef}>
            Join the GameMax Community
            <span style={{ opacity: 0 }}>.</span>
          </h2>
        </div>
        
        <div className={styles.newsletterContent}>
          <p>Subscribe to our newsletter for the latest updates on GAM token, exclusive offers, and community events.</p>
          
          <form ref={formRef} className={styles.newsletterForm} onSubmit={handleSubmit}>
            <div className={styles.inputWrapper}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                required
                disabled={isSubmitting}
                className={styles.emailInput}
              />
              <button 
                type="submit" 
                className={styles.submitButton}
                disabled={isSubmitting}
                ref={buttonRef}
              >
                {isSubmitting ? (
                  <span>Joining...</span>
                ) : (
                  <span>Join Now!</span>
                )}
              </button>
            </div>
            
            {submitStatus && (
              <div className={styles.statusMessage}>
                <p className={submitStatus === 'success' ? styles.successMessage : styles.errorMessage}>
                  {statusMessage}
                </p>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default Newsletter; 
